import { State, process } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { isArray } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import InvoiceApproversColumns from 'src/assets/static/invoice_details/InvoiceApproversColumns';
import GridWrapper from 'src/components/GridWrapper';
import { InvoiceApproverItem } from 'src/services/useGetInvoiceApprovers';
import { SetState } from 'src/types/generic';

const INVOICE_APPROVAL_DATA_ITEM: keyof InvoiceApproverItem = 'clientId';
const initialInvoiceApprovalState: State = {
  sort: [],
  take: 10,
  skip: 0,
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: true,
  mode: 'single',
};

const defaultProps = {
  tableTextId: 'Approvers',
  className: 'no_Veritcal_ScrollBar',
  DataItemKey: INVOICE_APPROVAL_DATA_ITEM,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  GridPagerSettings: gridPagerSettings,
  fieldModifiable: true,
  height: '250px',
} as const;

interface IInvoiceApproversTable {
  setSelectedApprover: SetState<InvoiceApproverItem>;
  invoiceApprovers: InvoiceApproverItem[];
  isApproversLoading: boolean;
  setInvoiceApprovers: SetState<InvoiceApproverItem[]>;
}

const InvoiceApproversTable = ({
  setSelectedApprover,
  invoiceApprovers,
  isApproversLoading,
  setInvoiceApprovers,
}: IInvoiceApproversTable) => {
  const [invoiceApprovalState, setInvoiceApprovalState] = useState(initialInvoiceApprovalState);
  const [latestClickedId, setLatestClickedId] = useState(0);

  const { data, total } = useMemo(
    () => process(invoiceApprovers || [], invoiceApprovalState),
    [invoiceApprovalState, invoiceApprovers]
  );

  const onInvoiceApprovalStateChange = useCallback(
    (e: GridDataStateChangeEvent) => {
      setInvoiceApprovalState(e.dataState);
      setInvoiceApprovers((prev: any) =>
        prev.map((approve: any) => ({ ...approve, selected: false }))
      );
    },
    [setInvoiceApprovers]
  );

  const onApproversSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      const selectedClientId = event.dataItems[event.startRowIndex];
      setInvoiceApprovers((prev: any) =>
        prev.map((item: any) => ({
          ...item,
          selected: item[INVOICE_APPROVAL_DATA_ITEM] === selectedClientId.clientId,
        }))
      );
    },
    [setInvoiceApprovers]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const { checked } = checkboxElement;
      setInvoiceApprovers((prev: any) =>
        prev?.map((element: any) => ({ ...element, selected: checked }))
      );
    },
    [setInvoiceApprovers]
  );

  const cellClickFns = useCallback(
    () => ({ latestClickedId, setLatestClickedId }),
    [latestClickedId]
  );

  const isFilterApplied = useMemo(() => {
    if (
      invoiceApprovalState?.filter === undefined ||
      invoiceApprovalState.filter.filters === undefined
    ) {
      return false;
    } else if (isArray(invoiceApprovalState.filter.filters)) {
      return true;
    }
  }, [invoiceApprovalState.filter]);

  useEffect(() => {
    setSelectedApprover(data?.find((item) => item?.selected) as InvoiceApproverItem);
  }, [data, setSelectedApprover, isFilterApplied]);

  return (
    <>
      <div className="mt-1 mb-1 px-1">
        {isApproversLoading || data === null ? (
          <div className="h-[50px]">
            <div className="k-loading-image " />
          </div>
        ) : data !== null && invoiceApprovers.length > 0 ? (
          <GridWrapper
            columns={InvoiceApproversColumns}
            total={total}
            dataSource={data}
            dataState={invoiceApprovalState}
            onDataStateChange={onInvoiceApprovalStateChange}
            onSelectionChange={onApproversSelectionChange}
            onHeaderSelectionChange={onHeaderSelectionChange}
            pageable={gridPagerSettings}
            loading={isApproversLoading}
            cellClick={cellClickFns}
            noRecordText={isFilterApplied ? 'No records available' : ''}
            {...defaultProps}
          />
        ) : (
          <div className="font-bold">
            Please contact customer support to add a document approver for your account.
          </div>
        )}
      </div>
    </>
  );
};

export default InvoiceApproversTable;
